import React, {useMemo} from "react";
import PublicLayout from "modules/ui/components/PublicLayout";
import useTranslate from "../../modules/core/languages/hooks/useTranslate";
import {useParams} from "react-router-dom";
import useBookingInfoGet from "../../modules/booking/hooks/useBookingInfoGet";
import {Alert} from "../../modules/ui/shared/Alert/Alert";
import ButtonPrimary from "../../modules/ui/shared/Button/ButtonPrimary";
import useCustomerContext from "../../modules/customers/hooks/useCustomerContext";
import useHotelGet from "../../modules/hotels/hooks/useHotelGet";
import useCityGet from "../../modules/cities/hooks/useCityGet";
import useEventGet from "../../modules/events/hooks/useEventGet";
import useGetBookingItems from "../../modules/booking/hooks/useGetBookingItems";
import Booking from "../../modules/booking/types/Booking";
import {HOTEL_NOT_SHOWING_PAYMENT_REQUIRED_DISCLAIMER} from "../../config/config";
import {formatNumber} from "../../modules/common/utils/number";
import {SalesChannelEnum} from "../../modules/common/enums/SalesChannelEnum";
import useEventCityGet from "../../modules/eventCities/hooks/useEventCityGet";
import {
    BuildingOffice2Icon,
    CreditCardIcon,
    EnvelopeIcon,
    HashtagIcon,
    MapPinIcon,
    PhoneIcon,
    TrophyIcon,
    UserIcon,
} from "@heroicons/react/24/outline";
import OverbookingAlert from "../../modules/booking/components/OverbookingAlert";
import RoomDetailsCard from "../../modules/booking/components/RoomDetailsCard";
import TransferCard from "../../modules/booking/components/TransferCard";
import InfoBlock from "../../modules/booking/components/InfoBlock";
import InfoCard from "../../modules/booking/components/InfoCard";
import PriceSummary from "../../modules/payment/components/PriceSummary";
import PaymentPanel, {PaymentPanelPaymentInput} from "../../modules/payment/components/PaymentPanel";
import {useOidcClientContext} from "../../modules/ui/context/OidcClientContext";
import useEventCityHotelsDetailsGet from "../../modules/eventHotels/hooks/useEventCityHotelGet";
import moment from "moment/moment";
import {Trans} from "react-i18next";
import {CreditCardLogos} from "../../modules/payment/components/CreditCardLogos";
import useBookingHotelGet from "../../modules/booking/hooks/useBookingHotelGet";
import useBookingPayWithSavedCard from "../../modules/booking/hooks/useBookingPayWithSavedCard";
import {Tpv} from "../../modules/payment/enum/Tpv";

function PageBookOk() {
    const {bookingInternalId} = useParams();
    const t = useTranslate();
    const {languageCode, customer} = useCustomerContext();
    const {authenticated} = useOidcClientContext();

    const {data: bookingInfo, isLoading: isLoadingBookingInfo} = useBookingInfoGet(bookingInternalId);

    const bookingCardUuid = useMemo(() => {
        if (!bookingInfo?.paymentCard) return null;
        const parts = bookingInfo.paymentCard.split('/');
        return parts[parts.length - 1] || null;
    }, [bookingInfo?.paymentCard]);

    const {data: hotelInfo} = useHotelGet(bookingInfo?.hotel ?? 0, {execute: !isLoadingBookingInfo});
    const {data: cityInfo} = useCityGet(bookingInfo?.city ?? 'cities/0', {execute: !isLoadingBookingInfo});
    const {data: eventInfo} = useEventGet(bookingInfo?.event ?? 0, {execute: !isLoadingBookingInfo});
    const cityEventId = bookingInfo?.cityEvent?.split('/').pop();
    const {data: cityEventInfo} = useEventCityGet(cityEventId, {execute: !!cityEventId && !isLoadingBookingInfo});
    const {data: bookingItems} = useGetBookingItems(bookingInfo?.internalId, {execute: !isLoadingBookingInfo});
    const {data: hotelEvent} = useEventCityHotelsDetailsGet(cityEventInfo?.id, hotelInfo?.id, {execute: !!cityEventInfo?.id && !!hotelInfo?.id});
    const {data: bookingHotel} = useBookingHotelGet(bookingInfo?.bookingHotel ?? '', {execute: !isLoadingBookingInfo});

    const payWithCard = useBookingPayWithSavedCard();

    const isMoreThanAMonth: boolean | null = useMemo(() => {
        if (!cityEventInfo) return null;
        if (!cityEventInfo.eventStartDate) return true;
        const diffInDays = (new Date(cityEventInfo.eventStartDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24);
        return diffInDays >= 30;
    }, [cityEventInfo]);

    const showDirectPaymentMessage = (b: Booking) =>
        b.status !== 'canceled' && b.paymentMethod === 'direct_payment' && b.hotel !== HOTEL_NOT_SHOWING_PAYMENT_REQUIRED_DISCLAIMER;

    const showPaymentPanel = (b?: Booking): boolean => {
        if (!b) return false;
        if (b.paymentMethod !== 'payment_request') return false;
        if (customer.type !== 2) return false;
        if (b.paymentStatus === 'payment_requested') return true;
        if (b.paymentStatus === 'payment_scheduled') return false;
        return b.status !== 'confirmed' && b.status !== 'canceled' && !b.overbooking;
    };

    const handleCardPay = async (input: PaymentPanelPaymentInput) => {
        if (!bookingInfo) return;
        const result = await payWithCard({
            bookingId: '/bookings/' + bookingInfo.id,
            bookingInternalId: bookingInfo.internalId,
            tpv: Tpv.PAYCOMET,
            paymentCardUuid: '/web/payment_cards/' + input.selectedCardUuid,
            ...(input.payMode === "later" && input.payLaterIso ? {scheduledAt: input.payLaterIso} : {}),
        });
        if (result.paymentLink) window.location.href = result.paymentLink;
    };

    const handleLuxonPay = async () => {
        if (!bookingInfo) return;
        const result = await payWithCard({
            bookingId: '/bookings/' + bookingInfo.id,
            bookingInternalId: bookingInfo.internalId,
            tpv: Tpv.LUXON,
        });
        if (result.paymentLink) window.location.href = result.paymentLink;
    };

    if (!bookingInfo || !hotelInfo || !cityInfo || !eventInfo || !cityEventInfo || isMoreThanAMonth === null || !bookingItems)
        return <PublicLayout/>;

    const hasPaymentPanel = showPaymentPanel(bookingInfo) && bookingInfo.paymentDifference > 0;
    // El lateral siempre se muestra salvo cuando la reserva está cancelada (PriceSummary devuelve null)
    const hasSidebar = bookingInfo.status !== 'canceled' && Number(bookingInfo.totalBasket) !== 0;

    return (
        <PublicLayout>
            <div className="nc-CheckOutPage" data-nc-id="CheckOutPage">
                <main className="container mt-11 mb-12 lg:mb-18">

                    {/* ── Título y alertas a ancho completo ── */}
                    <div className="flex flex-col space-y-4 px-0 sm:px-2 mb-6">
                        <h2 className="text-3xl lg:text-4xl font-semibold">{t("bookOk.yourBooking")}</h2>

                        {bookingInfo.status === 'confirmed' && (
                            <Alert type="success" closable={false}>
                                <p>{bookingInfo.paymentStatus === 'paid' ? t("bookOk.bookingConfirmedAndPaid") : t("bookOk.bookingConfirmed")}</p>
                            </Alert>
                        )}

                        {bookingInfo.status === 'pending' && bookingInfo.paymentStatus === 'payment_scheduled' && (
                            <div className="mb-2">
                                <h5 className="text-2xl lg:text-2xl font-semibold mt-2 mb-4">
                                    {t("bookOk.statusBookingNotOverbooking")}
                                </h5>
                                <Alert type="warning" closable={false}>
                                    <p>{t("bookOk.bookingScheduledPayment", {
                                        nextPaymentAmount: `${formatNumber(bookingInfo.paymentDifference)} ${bookingInfo.currencySymbol}`,
                                        nextPaymentDate: moment(bookingInfo.paymentScheduledAt).format("DD/MM/YYYY"),
                                    })}</p>
                                </Alert>
                            </div>
                        )}

                        {bookingInfo.status === 'pending' && bookingInfo.overbooking && bookingInfo.paymentStatus !== 'payment_requested' && bookingInfo.paymentStatus !== 'payment_scheduled' && (
                            <OverbookingAlert bookingItems={bookingItems}/>
                        )}

                        {showDirectPaymentMessage(bookingInfo) && Number(bookingInfo.totalBasket) !== 0 && (
                            <Alert type="success" closable={false}>
                                <p>{t("bookOk.rememberPayHotel")} {formatNumber(bookingInfo.totalBasket)} <span dangerouslySetInnerHTML={{__html: bookingInfo.currencySymbol}}/></p>
                            </Alert>
                        )}

                        {bookingInfo.status === 'canceled' && (
                            <Alert type="error" closable={false}>
                                <p>{bookingInfo.paymentStatus === 'refunded' ? t("bookOk.boolingCanceledAndRefunded") : t("bookOk.bookingCanceled")}</p>
                            </Alert>
                        )}

                        {hasPaymentPanel && (
                            <div className="mb-2">
                                <h5 className="text-2xl lg:text-2xl font-semibold mt-2 mb-4">
                                    {t("bookOk.statusBookingNotOverbooking")}
                                </h5>
                                <Alert type="warning" closable={false} containerClassName="mb-5">
                                    {!isMoreThanAMonth || bookingInfo?.paymentStatus === 'payment_requested' ? (
                                        <p>{t("bookOk.needToPay")}</p>
                                    ) : (
                                        <p>
                                            <Trans
                                                i18nKey="bookOk.rememberPay"
                                                components={{
                                                    PayCometIcon: (
                                                        <CreditCardLogos
                                                            size={35}
                                                            height={35}
                                                            className="inline"
                                                        />
                                                    ),
                                                }}
                                            />
                                        </p>
                                    )}
                                </Alert>
                            </div>
                        )}
                    </div>

                    {/* ── Layout de dos columnas ── */}
                    <div className={`flex flex-col ${hasSidebar ? 'lg:flex-row lg:items-start lg:gap-10' : ''} px-0 sm:px-2`}>

                        {/* ── Columna izquierda: detalles de la reserva ── */}
                        <div className={`flex flex-col space-y-6 ${hasSidebar ? 'w-full lg:w-3/5 xl:w-2/3' : 'w-full'}`}>

                            {cityEventInfo?.transfer && bookingInfo.status === 'confirmed' && bookingInfo.salesChannel === SalesChannelEnum.WEB && (
                              <TransferCard eventInfo={eventInfo} cityInfo={cityInfo} cityEventInfo={cityEventInfo}/>
                            )}

                            {/* ── Booking details ── */}
                            <InfoCard title={t("booking.bookingDetails")} subtitle={t("booking.bookingDetailsSubtitle")}>
                                <InfoBlock icon={<HashtagIcon className="w-4 h-4 text-indigo-500"/>} label={t("bookOk.confirmationNumber")} value={<span className="font-bold">#{bookingInfo.id}</span>}/>
                                <InfoBlock icon={<UserIcon className="w-4 h-4 text-indigo-500"/>} label={t("bookOk.nameAndSurnames")} value={`${bookingInfo.name} ${bookingInfo.surname}`}/>
                                <InfoBlock icon={<EnvelopeIcon className="w-4 h-4 text-indigo-500"/>} label={t("bookOk.email")} value={bookingInfo.email}/>
                                <InfoBlock icon={<PhoneIcon className="w-4 h-4 text-indigo-500"/>} label={t("bookOk.telephone")} value={`+${bookingInfo.dialCode} ${bookingInfo.phoneNumber}`}/>
                            </InfoCard>

                            {/* ── Hotel details ── */}
                            <InfoCard title={t("bookOk.hotelDetails")} subtitle={t("bookOk.hotelDetailsSubtitle")}>
                                <InfoBlock icon={<TrophyIcon className="w-4 h-4 text-amber-500"/>} label={t("bookOk.event")} value={eventInfo?.name}/>
                                <InfoBlock icon={<MapPinIcon className="w-4 h-4 text-amber-500"/>} label={t("bookOk.city")} value={cityInfo?.trName[languageCode]}/>
                                <InfoBlock icon={<BuildingOffice2Icon className="w-4 h-4 text-amber-500"/>} label={t("bookOk.hotel")} value={hotelInfo?.name}/>
                                <InfoBlock icon={<MapPinIcon className="w-4 h-4 text-amber-500"/>} label={t("bookOk.address")} value={hotelInfo?.address}/>
                                {bookingInfo.paymentMethod === 'direct_payment' && (
                                    <InfoBlock icon={<CreditCardIcon className="w-4 h-4 text-amber-500"/>} label={t("bookOk.payMethod")} value={t("bookOk.payOnHotel")} fullWidth/>
                                )}
                            </InfoCard>

                            <RoomDetailsCard bookingItems={bookingItems} hotelInfo={hotelInfo} currencySymbol={bookingInfo.currencySymbol}/>

                            <div className="text-center mt-4">
                                <ButtonPrimary className="uppercase" href="/">
                                    {t("checkout.continueShopping")}
                                </ButtonPrimary>
                            </div>
                        </div>

                        {/* ── Columna derecha: panel financiero (móvil: debajo del contenido, desktop: sidebar sticky) ── */}
                        {/* IMPORTANTE: PaymentPanel se renderiza UNA SOLA VEZ para evitar IDs duplicados en el DOM */}
                        {/* que romperían la inicialización de los iframes de Paycomet (getElementById retorna solo el primero) */}
                        {hasSidebar && (
                            <div className="flex flex-col w-full lg:w-2/5 xl:w-1/3 flex-shrink-0">
                                <div className="lg:sticky lg:top-6 flex flex-col space-y-4">
                                    <PriceSummary bookingInfo={bookingInfo} cityTax={bookingHotel?.taxRate ?? 0} hidePaymentPending={hasPaymentPanel}/>
                                    {hasPaymentPanel && (
                                        <PaymentPanel
                                            bookingId={bookingInfo.id}
                                            bookingInternalId={bookingInfo.internalId}
                                            amount={bookingInfo.paymentDifference}
                                            currencySymbol={bookingInfo.currencySymbol}
                                            currency={bookingInfo.currency || ''}
                                            authenticated={authenticated}
                                            preferredCardUuid={bookingCardUuid}
                                            tpvPaymentLastDate={hotelEvent?.tpvPaymentLastDate}
                                            tpvPaymentInitialPayPercentage={hotelEvent?.tpvPaymentInitialPayPercentage}
                                            onCardPay={handleCardPay}
                                            onLuxonPay={handleLuxonPay}
                                        />
                                    )}
                                </div>
                            </div>
                        )}

                    </div>
                </main>
            </div>
        </PublicLayout>
    );
}

export default PageBookOk;
